import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { activitiesApi, Activity } from '../api/activities';
import { leadsApi, Lead } from '../api/leads';
import { accountsApi, Account } from '../api/accounts';
import { contactsApi, Contact } from '../api/contacts';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/PremiumLayout';
import { 
  PlusIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  CalendarIcon, 
  DocumentTextIcon, 
  PencilIcon, 
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

interface ClientGroup {
  id: string;
  name: string;
  type: 'account' | 'lead' | 'contact';
  entityId: number;
  activities: Activity[];
  lastActivityDate: string;
  activityCount: number;
}

const Activities: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientGroup | null>(null);
  const [viewMode, setViewMode] = useState<'clients' | 'timeline'>('clients');
  const [filters, setFilters] = useState({
    activityType: 'all' as 'all' | 'call' | 'email' | 'meeting' | 'note',
    dateRange: 'all' as 'all' | 'today' | 'week' | 'month',
    searchTerm: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'call',
    subject: '',
    description: '',
    entity_type: 'none' as 'none' | 'lead' | 'account' | 'contact',
    lead_id: '',
    account_id: '',
    contact_id: '',
    scheduled_date: '',
    scheduled_time: '',
  });

  const queryClient = useQueryClient();

  const { data: activities = [], isLoading } = useQuery({
    queryKey: ['activities'],
    queryFn: activitiesApi.getAll,
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: contactsApi.getAll,
  });

  // Filter activities based on filters
  const filteredActivities = useMemo(() => {
    let filtered = [...activities];

    // Filter by activity type
    if (filters.activityType !== 'all') {
      filtered = filtered.filter(a => a.type === filters.activityType);
    }

    // Filter by date range
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(a => {
        const activityDate = new Date(a.created_at);
        switch (filters.dateRange) {
          case 'today':
            return activityDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return activityDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return activityDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        a.subject.toLowerCase().includes(searchLower) ||
        a.description?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [activities, filters]);

  // Group activities by client
  const clientGroups = useMemo(() => {
    const groups: Map<string, ClientGroup> = new Map();

    filteredActivities.forEach(activity => {
      let clientKey = '';
      let clientName = '';
      let clientType: 'account' | 'lead' | 'contact' = 'account';
      let entityId = 0;

      if (activity.account_id) {
        const account = accounts.find(a => a.id === activity.account_id);
        if (account) {
          clientKey = `account-${activity.account_id}`;
          clientName = account.name;
          clientType = 'account';
          entityId = activity.account_id;
        }
      } else if (activity.lead_id) {
        const lead = leads.find(l => l.id === activity.lead_id);
        if (lead) {
          clientKey = `lead-${activity.lead_id}`;
          // Get account name if available (company/account name is priority)
          const account = accounts.find(a => a.id === lead.account_id);
          const contactName = lead.first_name && lead.last_name
            ? `${lead.first_name} ${lead.last_name}`
            : lead.first_name || lead.lead_name || '';
          clientName = account?.name || lead.project_name || lead.brand || lead.name || contactName || `Lead #${activity.lead_id}`;
          clientType = 'lead';
          entityId = activity.lead_id;
        }
      } else if (activity.contact_id) {
        const contact = contacts.find(c => c.id === activity.contact_id);
        if (contact) {
          clientKey = `contact-${activity.contact_id}`;
          clientName = `${contact.first_name} ${contact.last_name}`;
          clientType = 'contact';
          entityId = activity.contact_id;
        }
      } else {
        // Unassigned activities
        clientKey = 'unassigned';
        clientName = 'Unassigned';
        clientType = 'account';
        entityId = 0;
      }

      if (clientKey) {
        if (!groups.has(clientKey)) {
          groups.set(clientKey, {
            id: clientKey,
            name: clientName,
            type: clientType,
            entityId,
            activities: [],
            lastActivityDate: activity.created_at,
            activityCount: 0,
          });
        }
        const group = groups.get(clientKey)!;
        group.activities.push(activity);
        group.activityCount = group.activities.length;
        // Update last activity date
        if (new Date(activity.created_at) > new Date(group.lastActivityDate)) {
          group.lastActivityDate = activity.created_at;
        }
      }
    });

    // Sort by last activity date (most recent first)
    return Array.from(groups.values()).sort((a, b) => 
      new Date(b.lastActivityDate).getTime() - new Date(a.lastActivityDate).getTime()
    );
  }, [filteredActivities, accounts, leads, contacts]);

  // Get activities for selected client
  const selectedClientActivities = useMemo(() => {
    if (!selectedClient) return [];
    return selectedClient.activities.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [selectedClient]);

  const createMutation = useMutation({
    mutationFn: activitiesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Activity> }) =>
      activitiesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      setShowModal(false);
      setEditingActivity(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: activitiesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      if (selectedClient) {
        // Refresh selected client
        const updated = clientGroups.find(c => c.id === selectedClient.id);
        if (updated) setSelectedClient(updated);
      }
    },
  });

  const resetForm = () => {
    setFormData({
      type: 'call',
      subject: '',
      description: '',
      entity_type: 'none',
      lead_id: '',
      account_id: '',
      contact_id: '',
      scheduled_date: '',
      scheduled_time: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const activityData: any = {
      type: formData.type,
      subject: formData.subject,
      description: formData.description,
      created_by: user?.id,
    };

    // Add scheduled date/time if provided
    if (formData.scheduled_date) {
      activityData.scheduled_date = formData.scheduled_date;
    }
    if (formData.scheduled_time) {
      activityData.scheduled_time = formData.scheduled_time;
    }

    if (formData.entity_type === 'lead' && formData.lead_id) {
      activityData.lead_id = parseInt(formData.lead_id);
    } else if (formData.entity_type === 'account' && formData.account_id) {
      activityData.account_id = parseInt(formData.account_id);
    } else if (formData.entity_type === 'contact' && formData.contact_id) {
      activityData.contact_id = parseInt(formData.contact_id);
    }

    if (editingActivity) {
      updateMutation.mutate({ id: editingActivity.id, data: activityData });
    } else {
      createMutation.mutate(activityData);
    }
  };

  const handleEdit = (activity: Activity) => {
    setEditingActivity(activity);
    let entityType: 'none' | 'lead' | 'account' | 'contact' = 'none';
    if (activity.lead_id) entityType = 'lead';
    else if (activity.account_id) entityType = 'account';
    else if (activity.contact_id) entityType = 'contact';

    // Extract date and time from scheduled_date if it exists
    let scheduledDate = '';
    let scheduledTime = '';
    if ((activity as any).scheduled_date) {
      const dateStr = (activity as any).scheduled_date;
      if (dateStr.includes('T')) {
        const [date, time] = dateStr.split('T');
        scheduledDate = date;
        scheduledTime = time.substring(0, 5); // HH:MM format
      } else {
        scheduledDate = dateStr;
      }
    }

    setFormData({
      type: activity.type,
      subject: activity.subject,
      description: activity.description || '',
      entity_type: entityType,
      lead_id: activity.lead_id?.toString() || '',
      account_id: activity.account_id?.toString() || '',
      contact_id: activity.contact_id?.toString() || '',
      scheduled_date: scheduledDate,
      scheduled_time: scheduledTime,
    });
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this activity? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <PhoneIcon className="h-5 w-5 text-blue-500" />;
      case 'email':
        return <EnvelopeIcon className="h-5 w-5 text-green-500" />;
      case 'meeting':
        return <CalendarIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'call':
        return 'bg-blue-50 border-blue-200';
      case 'email':
        return 'bg-green-50 border-green-200';
      case 'meeting':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getClientIcon = (type: 'account' | 'lead' | 'contact') => {
    switch (type) {
      case 'account':
        return <BuildingOfficeIcon className="h-5 w-5 text-indigo-500" />;
      case 'lead':
        return <ChartBarIcon className="h-5 w-5 text-orange-500" />;
      case 'contact':
        return <UserGroupIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Activities">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Activities"
      subtitle={`${activities.length} total activities • ${clientGroups.length} clients`}
      actions={
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary text-sm px-4 py-2 ${showFilters ? 'bg-indigo-100 text-indigo-700' : ''}`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingActivity(null);
              setShowModal(true);
            }}
            className="btn-primary text-sm px-4 py-2"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Log Activity
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Filters Panel */}
        {showFilters && (
          <div className="premium-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Filter Activities</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Activity Type
                </label>
                <select
                  value={filters.activityType}
                  onChange={(e) => setFilters({ ...filters, activityType: e.target.value as any })}
                  className="input-premium text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="call">📞 Calls</option>
                  <option value="email">✉️ Emails</option>
                  <option value="meeting">📅 Meetings</option>
                  <option value="note">📝 Notes</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Date Range
                </label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as any })}
                  className="input-premium text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="input-premium text-sm"
                  placeholder="Search activities..."
                />
              </div>
            </div>
            {(filters.activityType !== 'all' || filters.dateRange !== 'all' || filters.searchTerm) && (
              <div className="mt-4 flex items-center space-x-2">
                <button
                  onClick={() => setFilters({ activityType: 'all', dateRange: 'all', searchTerm: '' })}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Clear Filters
                </button>
                <span className="text-sm text-gray-500">
                  Showing {filteredActivities.length} of {activities.length} activities
                </span>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        {selectedClient ? (
          // Client Detail View
          <div className="space-y-4">
            <div className="premium-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setSelectedClient(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                  <div className="flex items-center space-x-2">
                    {getClientIcon(selectedClient.type)}
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{selectedClient.name}</h2>
                      <p className="text-xs text-gray-500 capitalize">{selectedClient.type} • {selectedClient.activityCount} activities</p>
                    </div>
                  </div>
                  {/* View Lead/Account button */}
                  {selectedClient.entityId > 0 && (
                    <button
                      onClick={() => navigate(selectedClient.type === 'lead' ? `/leads/${selectedClient.entityId}` : `/accounts/${selectedClient.entityId}`)}
                      className="ml-4 flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1" />
                      View {selectedClient.type === 'lead' ? 'Lead' : 'Account'}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => {
                    const newFormData: any = {
                      type: 'call',
                      subject: '',
                      description: '',
                      entity_type: selectedClient.type,
                      lead_id: '',
                      account_id: '',
                      contact_id: '',
                    };
                    if (selectedClient.type === 'account') {
                      newFormData.account_id = String(selectedClient.entityId);
                    } else if (selectedClient.type === 'lead') {
                      newFormData.lead_id = String(selectedClient.entityId);
                    } else if (selectedClient.type === 'contact') {
                      newFormData.contact_id = String(selectedClient.entityId);
                    }
                    setFormData(newFormData);
                    setEditingActivity(null);
                    setShowModal(true);
                  }}
                  className="btn-primary text-sm px-4 py-2"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Activity
                </button>
              </div>
            </div>

            {/* Activities Timeline */}
            <div className="premium-card overflow-hidden">
              <div className="divide-y divide-gray-100">
                {selectedClientActivities.map((activity, index) => (
                  <div key={activity.id} className={`p-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-all group border-l-4 ${getActivityColor(activity.type)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{activity.subject}</h3>
                            <span className="text-sm text-gray-500 ml-4">
                              {new Date(activity.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              {activity.type}
                            </span>
                          </div>
                          {activity.description && (
                            <p className="text-sm text-gray-700 mt-2">{activity.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEdit(activity)}
                          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(activity.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {selectedClientActivities.length === 0 && (
                  <div className="p-12 text-center">
                    <p className="text-gray-500">No activities for this client</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Client List View
          <div className="premium-card overflow-hidden">
            <div className="divide-y divide-gray-200">
              {clientGroups.map((client) => (
                <div
                  key={client.id}
                  onClick={() => setSelectedClient(client)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {getClientIcon(client.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-base font-semibold text-gray-900 truncate">{client.name}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 capitalize font-medium">
                            {client.type}
                          </span>
                        </div>
                        {client.activities.length > 0 && (
                          <div className="mt-1.5 flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              {getActivityIcon(client.activities[0].type)}
                              <span className="truncate max-w-xs">{client.activities[0].subject}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-6 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">{client.activityCount}</div>
                        <div className="text-xs text-gray-500">activities</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-700">
                          {new Date(client.lastActivityDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(client.lastActivityDate).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {clientGroups.length === 0 && (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CalendarIcon className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No activities found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or log your first activity.</p>
                <button
                  onClick={() => {
                    resetForm();
                    setEditingActivity(null);
                    setShowModal(true);
                  }}
                  className="btn-primary"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Log Activity
                </button>
              </div>
            )}
          </div>
        )}

        {/* Activity Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-4 px-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all fade-in">
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {editingActivity ? 'Edit Activity' : 'Log Activity'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingActivity ? 'Update activity information' : 'Record an interaction or communication'}
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Activity Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="input-premium"
                    >
                      <option value="call">📞 Call</option>
                      <option value="email">✉️ Email</option>
                      <option value="meeting">📅 Meeting</option>
                      <option value="note">📝 Note</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="input-premium"
                      placeholder="Enter activity subject"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={5}
                      className="input-premium"
                      placeholder="Add activity details..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <CalendarIcon className="h-4 w-4 inline mr-1" />
                        Scheduled Date
                      </label>
                      <input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        className="input-premium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Scheduled Time
                      </label>
                      <input
                        type="time"
                        value={formData.scheduled_time}
                        onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                        className="input-premium"
                      />
                    </div>
                  </div>
                  {!selectedClient && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Link to Client (Optional)
                      </label>
                      <div className="space-y-3">
                        <div className="flex space-x-3">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="entity_type"
                              value="none"
                              checked={formData.entity_type === 'none'}
                              onChange={(e) => setFormData({ ...formData, entity_type: 'none', lead_id: '', account_id: '', contact_id: '' })}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">None</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="entity_type"
                              value="lead"
                              checked={formData.entity_type === 'lead'}
                              onChange={(e) => setFormData({ ...formData, entity_type: 'lead', account_id: '', contact_id: '' })}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Lead</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="entity_type"
                              value="account"
                              checked={formData.entity_type === 'account'}
                              onChange={(e) => setFormData({ ...formData, entity_type: 'account', lead_id: '', contact_id: '' })}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Account</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="entity_type"
                              value="contact"
                              checked={formData.entity_type === 'contact'}
                              onChange={(e) => setFormData({ ...formData, entity_type: 'contact', lead_id: '', account_id: '' })}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Contact</span>
                          </label>
                        </div>
                        {formData.entity_type === 'lead' && (
                          <select
                            value={formData.lead_id}
                            onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                            className="input-premium"
                          >
                            <option value="">Select a lead...</option>
                            {leads.map((lead) => {
                              // Get account name if available
                              const account = accounts.find(a => a.id === lead.account_id);
                              const displayName = account?.name || lead.project_name || lead.brand || lead.name;
                              return (
                                <option key={lead.id} value={lead.id}>
                                  {displayName} {(() => {
                                    const contactName = lead.first_name && lead.last_name
                                      ? `${lead.first_name} ${lead.last_name}`
                                      : lead.first_name || lead.lead_name || '';
                                    return contactName ? `(${contactName})` : '';
                                  })()} - {lead.stage}
                                </option>
                              );
                            })}
                          </select>
                        )}
                        {formData.entity_type === 'account' && (
                          <select
                            value={formData.account_id}
                            onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                            className="input-premium"
                          >
                            <option value="">Select an account...</option>
                            {accounts.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.name}
                              </option>
                            ))}
                          </select>
                        )}
                        {formData.entity_type === 'contact' && (
                          <select
                            value={formData.contact_id}
                            onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                            className="input-premium"
                          >
                            <option value="">Select a contact...</option>
                            {contacts.map((contact) => (
                              <option key={contact.id} value={contact.id}>
                                {contact.first_name} {contact.last_name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? 'Saving...'
                        : editingActivity
                        ? 'Update Activity'
                        : 'Log Activity'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingActivity(null);
                        resetForm();
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default Activities;
